import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";
import { dequal } from "dequal";
import pusher_pkg from "pusher-js";

import { ChannelsProvider } from "./ChannelsProvider.js";
import { __PusherContext } from "./PusherContext.js";
import type { PusherProviderProps } from "./types.js";

const Pusher = pusher_pkg as unknown as typeof pusher_pkg.default;
type Options = pusher_pkg.Options;

/**
 * Provider that creates your pusher instance and provides it to child hooks throughout your app.
 * Note, you can pass in value={{}} as a prop if you'd like to override the pusher client passed.
 * This is handy when simulating pusher locally, or for testing.
 * @param props Config for Pusher client
 */

export const CorePusherProvider: FC<
  PusherProviderProps & { children: ReactNode }
> = ({
  clientKey,
  triggerEndpoint,
  defer = false,
  children,
  _PusherRuntime,
  channelPrefix,
  ...props
}) => {
  // errors when required props are not passed.
  useEffect(() => {
    if (!clientKey) console.error("A client key is required for pusher");
    // if (!cluster) console.error("A cluster is required for pusher");
  }, [clientKey]);

  const config: Options = useMemo(() => ({ ...props }), [props]);

  // track config for comparison
  const previousConfig = useRef<Options | undefined>(props);
  useEffect(() => {
    previousConfig.current = props;
  });
  const [client, setClient] = useState<pusher_pkg.default>();

  useEffect(() => {
    // Skip creation of client if deferring, a value prop is passed, or config props are the same.
    if (
      !_PusherRuntime ||
      defer ||
      !clientKey ||
      props.value ||
      (dequal(previousConfig.current, props) && client !== undefined)
    ) {
      return;
    }

    setClient(new _PusherRuntime(clientKey, config));
  }, [client, clientKey, props, defer, _PusherRuntime, config]);

  // An empty prefix (an unprefixed build) means "no prefix" — normalise to
  // undefined so consumers never handle the empty string.
  const validatedChannelPrefix =
    (channelPrefix?.length ?? 0) === 0 ? undefined : channelPrefix;

  return (
    <__PusherContext.Provider
      value={{
        client,
        triggerEndpoint,
        channelPrefix: validatedChannelPrefix,
      }}
      {...props}
    >
      <ChannelsProvider>{children}</ChannelsProvider>
    </__PusherContext.Provider>
  );
};

export const PusherProvider: FC<
  PusherProviderProps & { children: ReactNode }
> = (props) => <CorePusherProvider _PusherRuntime={Pusher} {...props} />;
